import React, { useState } from 'react';
import { Briefcase, Hourglass, ArrowRightLeft, MessageSquare } from 'lucide-react';
import {
  ACTIVITY_TYPES, OUTCOMES, OPP_STAGES, activityLabel, labelFor, colorFor, showDay,
  EmptyMsg, AttachmentLinks,
} from './salesShared';
import { isProposalEvent } from '../../lib/clientPage';

// A client's sales history (activities, opportunities and assignment
// changes), newest first. Shared by the client window (ClientTimelineModal)
// and the client page (ClientDetailPage).

// One line of the assignment history, in plain words.
const assignmentText = (g) => {
  const who = g.by ? ` by ${g.by}` : '';
  if (g.reason === 'released_inactive') {
    return `Released from ${g.from_rep_name || 'its rep'}: ${(g.detail || 'no recent activity').toLowerCase()}`;
  }
  if (g.reason === 'claimed') return `Claimed by ${g.to_rep_name || g.by || 'a rep'}`;
  if (!g.to_rep_id) return `Unassigned from ${g.from_rep_name || 'its rep'}${who}`;
  if (!g.from_rep_id) return `Assigned to ${g.to_rep_name || 'a rep'}${who}`;
  return `Reassigned from ${g.from_rep_name || 'a rep'} to ${g.to_rep_name || 'a rep'}${who}`;
};

// data: { activities, opportunities, assignments, history_hidden, sales_rep }
export const clientEvents = (data) => data ? [
  ...(data.activities || []).map(a => ({ kind: 'activity', when: a.occurred_at || a.created_at, item: a })),
  ...(data.opportunities || []).map(o => ({ kind: 'opportunity', when: o.created_at, item: o })),
  ...(data.assignments || []).map(g => ({ kind: 'assignment', when: g.created_at, item: g })),
].sort((x, y) => (y.when || '').localeCompare(x.when || '')) : [];

const FILTER_BTN = 'text-xs px-2.5 py-1 rounded border';
const filterCls = (on) => `${FILTER_BTN} ${on ? 'border-[#3B82F6] bg-[#3B82F6]/15 text-[#F8FAFC]' : 'border-[#1E293B] text-[#94A3B8] hover:text-[#F8FAFC]'}`;

export const ClientHistoryList = ({ data, events: allEvents, salesOnly }) => {
  const othersClient = !!data?.history_hidden;
  const [onlyProposals, setOnlyProposals] = useState(false);
  const proposals = allEvents.filter(isProposalEvent);
  const events = onlyProposals ? proposals : allEvents;
  return (
    <div className="space-y-2">
    {!othersClient && allEvents.length > 0 && (
      <div className="flex items-center gap-2" role="group" aria-label="Filter the history" data-testid="history-filter">
        <button type="button" aria-pressed={!onlyProposals} onClick={() => setOnlyProposals(false)} className={filterCls(!onlyProposals)} data-testid="history-filter-all">All ({allEvents.length})</button>
        <button type="button" aria-pressed={onlyProposals} onClick={() => setOnlyProposals(true)} className={filterCls(onlyProposals)} data-testid="history-filter-proposals">Proposals ({proposals.length})</button>
      </div>
    )}
    <div className="border border-[#1E293B] rounded-lg divide-y divide-[#1E293B]" data-testid="timeline-events">
      {othersClient && (
        <EmptyMsg text={`This client is assigned to ${data.sales_rep?.name || 'another rep'}. Its history is visible to that rep and to managers.`} />
      )}
      {!othersClient && events.length === 0 && <EmptyMsg text={onlyProposals ? 'No proposals for this client yet.' : 'Nothing logged yet for this client.'} />}
      {events.map(ev => {
        if (ev.kind === 'opportunity') {
          const o = ev.item;
          const color = colorFor(OPP_STAGES, o.stage);
          return (
            <div key={`o-${o.id}`} className="flex items-start gap-3 p-3" data-testid={`timeline-opp-${o.id}`}>
              <Briefcase className="w-4 h-4 mt-0.5 text-[#A855F7] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#F8FAFC]">Opportunity: {o.title}</div>
                <div className="text-xs text-[#475569] mt-1">
                  <span style={{ color }}>{labelFor(OPP_STAGES, o.stage)}</span>
                  {' · '}${(o.estimated_value || 0).toLocaleString()}
                  {o.sales_rep_name ? ` · ${o.sales_rep_name}` : ''}
                  {' · '}{showDay(o.created_at)}
                </div>
              </div>
            </div>
          );
        }
        if (ev.kind === 'assignment') {
          const g = ev.item;
          const released = g.reason === 'released_inactive';
          return (
            <div key={`g-${g.id}`} className="flex items-start gap-3 p-3" data-testid={`timeline-assignment-${g.id}`}>
              {released
                ? <Hourglass className="w-4 h-4 mt-0.5 text-amber-400 shrink-0" />
                : <ArrowRightLeft className="w-4 h-4 mt-0.5 text-[#94A3B8] shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className={`text-sm ${released ? 'text-amber-300' : 'text-[#94A3B8]'}`}>{assignmentText(g)}</div>
                <div className="text-xs text-[#475569] mt-1">{new Date(g.created_at).toLocaleString()}</div>
              </div>
            </div>
          );
        }
        const a = ev.item;
        const Icon = ACTIVITY_TYPES.find(t => t.value === a.activity_type)?.icon || MessageSquare;
        const color = colorFor(OUTCOMES, a.outcome);
        return (
          <div key={`a-${a.id}`} className="flex items-start gap-3 p-3" data-testid={`timeline-activity-${a.id}`}>
            <Icon className="w-4 h-4 mt-0.5 text-[#3B82F6] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-[#F8FAFC]">{a.summary}</span>
                {a.created_by === 'newsdesk' && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide bg-[#3B82F6]/15 text-[#3B82F6]" data-testid={`timeline-newsdesk-${a.id}`}>
                    {a.newsdesk_direction === 'in' ? 'Email received' : 'Email sent'} · via Newsdesk
                  </span>
                )}
                {(a.status === 'planned' || a.status === 'cancelled') && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide bg-[#94A3B8]/15 text-[#CBD5E1]" data-testid={`timeline-status-${a.id}`}>
                    {a.status === 'planned' ? 'Planned' : 'Cancelled'}
                  </span>
                )}
                {a.outcome && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide" style={{ backgroundColor: `${color}22`, color }}>
                    {labelFor(OUTCOMES, a.outcome)}
                  </span>
                )}
              </div>
              {a.notes && <div className="text-xs text-[#94A3B8] mt-1 whitespace-pre-wrap">{a.notes}</div>}
              <AttachmentLinks activity={a} className="mt-1" />
              {a.created_by === 'newsdesk' && a.newsdesk_url && (
                <a href={a.newsdesk_url} target="_blank" rel="noreferrer" className="inline-block text-xs text-[#3B82F6] hover:underline mt-1" data-testid={`timeline-newsdesk-link-${a.id}`}>
                  Open in Newsdesk
                </a>
              )}
              <div className="text-xs text-[#475569] mt-1">
                {activityLabel(a.activity_type, salesOnly)}
                {a.sales_rep_name ? ` · ${a.sales_rep_name}` : ''}
                {a.accompanied_by_name ? ` · with ${a.accompanied_by_name}` : ''}
                {' · '}{new Date(a.occurred_at || a.created_at).toLocaleString()}
                {a.follow_up_date && !a.follow_up_completed && <span className="text-amber-400"> · follow up {showDay(a.follow_up_date)}</span>}
                {a.follow_up_completed && <span className="text-emerald-400"> · follow up done</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
    </div>
  );
};

export default ClientHistoryList;
